<template>
    <v-container>
        <v-row >
            <v-col cols="12" lg="5" offset-lg="2">
                <v-text-field
                    label="Specimen ID"
                    v-model="enteredSpecimenID"
                    @keyup.native="checkEnterKey"
                ></v-text-field>
            </v-col>
            <v-col cols="12" lg="3">
                <v-row>
                    <v-col cols="12" class="mr-1">
                        <v-btn
                            block
                            color="primary" 
                            :loading="loadingSpecimen" 
                            @click="loadSpecimen"
                        >
                            Go
                        </v-btn>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <v-row v-if="specimenLoaded" >
            <v-col cols="12">
                <v-card>
                    <v-card-title class="text-center">
                        <div class="title">Specimen: {{currentSpecimen.SpecimenDisplayID}}</div>
                    </v-card-title>
                    <v-card-text>
                        <v-row >
                            <v-col cols="12" sm="12" md="6" >
                                <v-row  class="specimen-details">
                                    <v-col cols="4" sm="3" class="specimen-details-header">
                                        Gender
                                    </v-col>
                                    <v-col cols="8" sm="9">
                                        {{ genderFull}}
                                    </v-col>
                                </v-row>
                                <v-row  :class="`specimen-details ${lastCleanClass}`">
                                    <v-col cols="4" sm="3" class="specimen-details-header">
                                        Last Clean
                                    </v-col>
                                    <v-col cols="8" sm="9">
                                        {{ lastCleanDateString }}
                                    </v-col>
                                </v-row>
                                <v-row  class="specimen-details">
                                    <v-col cols="4" sm="3" class="specimen-details-header">
                                        Location
                                    </v-col>
                                    <v-col cols="8" sm="9">
                                        {{ locationFullText }}
                                    </v-col>
                                </v-row>
                                <v-row  class="specimen-details">
                                    <v-col cols="4" sm="3" class="specimen-details-header">
                                        Supplier
                                    </v-col>
                                    <v-col cols="8" sm="9">
                                        {{ currentSpecimen.SupplierName}}
                                    </v-col>
                                </v-row>

                                <v-row >
                                    <v-col cols="12" v-if="currentSpecimen.Deceased">
                                        <v-alert
                                            :value="true"
                                            type="error"
                                            outlined
                                        >
                                            <span>Specimen is deceased. If this is incorrect, please contact a system administrator to mark the specimen as not deceased.</span>
                                        </v-alert>
                                    </v-col>
                                </v-row>
                            </v-col>
                            <v-col cols="12" sm="12" md="6" >
                                <DailyHusbandryWeightHistory :history="currentSpecimen.WeightHistory">
                                </DailyHusbandryWeightHistory>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <v-card>
                    <v-card-title class="text-center">
                        <div class="title">Analysis</div>
                    </v-card-title>
                    <v-card-text>
                        <VenomAnalysis :specimenID="currentSpecimen.SpecimenID"></VenomAnalysis>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import moment from 'moment';
import validationRules from "../../helpers/validationrules";
import DailyHusbandryWeightHistory from "./DailyHusbandryWeightHistory";
import GlobalEventBus from '@/globalEventBus';
import constants from 'comunet-btg-utah-repository-shared/constants';
import VenomAnalysis from '../venomanalysis/VenomAnalysis'

export default {
    components: {
        DailyHusbandryWeightHistory,
        VenomAnalysis
    },
    props: {
        specimenDisplayID: {
            type: String,
            default: null
        },
    },
    data() {
        return {
            currentSpecimen: {},
            rules: validationRules.rules,
            enteredSpecimenID: "",
            loadingSpecimen: false,
            AllowedFileExtensions: '.pdf, .docx, .doc',
            fileError: "",
            pendingFileName: "",
            pendingFile: null,
            pendingFileSaving: false,
            rowsPerPage: [5, 20, 50, 100],
            pagination: {
                rowsPerPage: 20, // default
                sortBy: "Date",
                descending: true
            },
            headers: [
                {
                    text: "File name",
                    value: "FileName"
                },
                {
                    text: "", //download button
                    sortable: false,
                    value: ""
                },
                {
                    text: "", // delete button
                    sortable: false,
                    width: "100px"
                }
            ]
        };
    },
    computed: {
        specimenLoaded() {
            return !this.loadingSpecimen && this.currentSpecimen != null && this.currentSpecimen.SpecimenID && this.currentSpecimen.SpecimenID > 0;
        },
        locationFullText() {
            return [this.currentSpecimen.LocationTypeText, this.currentSpecimen.RoomText, this.currentSpecimen.SuiteNumberText, this.currentSpecimen.RackNumber, this.currentSpecimen.DrawerText].filter(x => x).join(" - ");
        },
        genderFull() {
            if (!this.specimenLoaded){
                return "";
            }

            if (this.currentSpecimen.Gender === "M") {
                return "Male";
            }

            if (this.currentSpecimen.Gender === "F") {
                return "Female";
            }

            // gender can be null
            return "";
        },
        lastCleanDateString() {
            if (!this.specimenLoaded){
                return "";
            }

            if (!this.currentSpecimen.LastCleanDate){
                return "No clean recorded";
            } else {
                return moment.utc(this.currentSpecimen.LastCleanDate).format(constants.DATE_FORMAT_NO_TIME);
            }
        },
        lastCleanClass() {
            if (!this.specimenLoaded || !this.currentSpecimen.LastCleanDate){
                return "";
            }

            return moment.utc(this.currentSpecimen.LastCleanDate).add(30, 'd') > moment() ? "clean-date-valid" : "clean-date-invalid"
        }
    },
    methods: {
        checkEnterKey(event){
            if (event.keyCode === 13){
                this.loadSpecimen();
            }
        },
        loadSpecimen() {
            this.loadingSpecimen = true;
            this.currentSpecimen = {};

            let that = this;
            this.$store.dispatch("specimenStore/loadSpecimens", {
                params: {
                    SpecimenDisplayID: this.enteredSpecimenID
                },
                success: (data) => {
                    if (!(data.TotalSpecimens != 1)){
                        that.currentSpecimen = data.Specimens.map(function (l){
                            var species = that.$store.state.speciesStore.species.find(x => x.SpeciesID == l.SpeciesID);
                            l.Species = species;

                            var supplier = that.$store.state.supplierStore.suppliers.find(x => x.SupplierID == l.SupplierID);
                            l.Supplier = supplier;

                            return l;
                        })[0]

                        that.$router.replace('/venomprofile/' + that.currentSpecimen.SpecimenDisplayID)

                        that.validSpecimen = true;

                    }
                },
                finally: () => {
                    that.loadingSpecimen = false;
                }
            });
        },
        processScan(scan) {
            if (scan.specimenDisplayID != ""){
                this.enteredSpecimenID = scan.specimenDisplayID;
                this.loadSpecimen();
            }
        },
    },
    created() {
        Promise.all([
            this.$store.dispatch("supplierStore/loadSuppliers"),
            this.$store.dispatch("speciesStore/loadSpecies")
        ]).then(() => {
            if (this.specimenDisplayID){
                // Load all the specimen data
                this.enteredSpecimenID = this.specimenDisplayID;
                this.loadSpecimen();
            }
        },
        () => {
            if (this.specimenDisplayID){
                // Load all the specimen data
                this.enteredSpecimenID = this.specimenDisplayID;
                this.loadSpecimen();
            }
        });

        GlobalEventBus.$on('onScan', this.processScan);

    },
    beforeDestroy(){
        GlobalEventBus.$off('onScan', this.processScan);
    }
}
</script>

<style scoped>
    .clean-date-valid {
        color: green;
    }
    .clean-date-invalid {
        color: red;
    }
    .specimen-details .specimen-details-header {
        font-weight: bold;
    }

    .specimen-details .flex {
        min-height: 36px;
        font-size: 120%;
    }

    .force-small-column {
        width:1%
    }
</style>